import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  useToast,
  ButtonGroup,
  VStack,
  SimpleGrid,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { MaskedInput } from '../../../../../../../../../../../../../components/Form/MaskedInput';
import { translateError } from '../../../../../../../../../../../../../utils/errors';
import { createUserCategoriesService } from '../../../../../../../../../../../../../services/UserCategories/CreateUserCategoriesService';
import { IUserCategoryBase } from '../../../../../../../../../../../../../models/ventures';
import { useVenture } from '../../../../../../../../../../../../../hooks/venture';
import { updateUserCategoriesService } from '../../../../../../../../../../../../../services/UserCategories/UpdateUserCategoriesService';
import { NumberInput } from '../../../../../../../../../../../../../components/Form/NumberInput';
import {
  maskMoney,
  unmaskNumber,
} from '../../../../../../../../../../../../../utils/formatters/handleMask';

interface IHandleUserCategoryModalFormData {
  guestBookingPrice?: number;
  maxDayBookings?: number;
  maxGuestDayBookings?: number;
  title: string;
}

interface IHandleUserCategoryModalProps {
  category?: IUserCategoryBase;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const userCategoryFormSchema = Yup.object().shape({
  guestBookingPrice: Yup.number()
    .integer('Valores inteiros')
    .positive('Valores maiores que zero')
    .nullable()
    .transform((_, originalValue) =>
      originalValue === '' ? null : unmaskNumber(originalValue),
    ),
  maxDayBookings: Yup.number().when('maxGuestDayBookings', {
    is: (val?: number) => !!val,
    then: Yup.number()
      .nullable()
      .integer('Valores inteiros')
      .min(
        Yup.ref('maxGuestDayBookings'),
        'Minimum equal to maximum. Guest reservations',
      )
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
    otherwise: Yup.number()
      .nullable()
      .integer('Integer values')
      .positive('Values greater than zero')
      .transform((value, originalValue) =>
        originalValue === '' ? null : value,
      ),
  }),
  maxGuestDayBookings: Yup.number()
    .nullable()
    .integer('Integer values')
    .positive('Values greater than zero')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  title: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .required('Required'),
});

export const HandleUserCategoryModal = ({
  category,
  isOpen,
  onClose,
  onSave,
}: IHandleUserCategoryModalProps): JSX.Element => {
  const toast = useToast();
  const { venture, handleVenture } = useVenture();

  const { register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(userCategoryFormSchema),
  });

  const { errors } = formState;

  useEffect(() => {
    reset({
      guestBookingPrice: category?.guestBookingPrice
        ? maskMoney(category.guestBookingPrice)
        : undefined,
      maxDayBookings: category?.maxDayBookings,
      maxGuestDayBookings: category?.maxGuestDayBookings,
      title: category?.title,
    });
  }, [category, reset]);

  const handleVentureModalitySubmit: SubmitHandler<IHandleUserCategoryModalFormData> =
    useCallback(
      async ({
        guestBookingPrice,
        maxDayBookings,
        maxGuestDayBookings,
        title,
      }) => {
        if (venture) {
          if (!category) {
            try {
              const newUserCategory = await createUserCategoriesService({
                guestBookingPrice,
                maxDayBookings,
                maxGuestDayBookings,
                title,
                ventureId: venture.id,
              });

              const userCategories = [
                newUserCategory,
                ...venture.userCategories,
              ];

              handleVenture({
                ...venture,
                userCategories,
              });

              reset({
                guestBookingPrice: null,
                maxDayBookings: null,
                maxGuestDayBookings: null,
                title: null,
              });

              onSave();

              toast({
                title: 'Successfully registered',
                description: 'Category registered successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Registration failed',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'An error occurred while registering the category, please try again.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          } else {
            try {
              const updatedCategory = await updateUserCategoriesService({
                categoryId: category.id,
                guestBookingPrice,
                maxDayBookings,
                maxGuestDayBookings,
                title,
              });

              handleVenture({
                ...venture,
                userCategories: venture.userCategories.map((ventureModality) =>
                  ventureModality.id === updatedCategory.id
                    ? { ...ventureModality, ...updatedCategory }
                    : ventureModality,
                ),
              });

              reset({
                guestBookingPrice: null,
                maxDayBookings: null,
                maxGuestDayBookings: null,
                title: null,
              });

              onSave();

              toast({
                title: 'Updated successfully',
                description: 'Category updated successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Update failed',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'An error occurred while updating the category, please try again.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          }
        }
      },
      [category, handleVenture, onSave, reset, toast, venture],
    );

  const handleCloseModal = useCallback(() => {
    reset({
      guestBookingPrice: null,
      maxDayBookings: null,
      maxGuestDayBookings: null,
      title: null,
    });

    onClose();
  }, [onClose, reset]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={handleCloseModal}>
      <ModalOverlay />

      <ModalContent
        as="form"
        onSubmit={handleSubmit(handleVentureModalitySubmit)}
      >
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            Member category
          </Heading>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <VStack spacing="8">
            <MaskedInput
              label="Título"
              error={errors.title}
              {...register('title')}
            />

            <SimpleGrid
              columns={[1, 1, 3]}
              spacing="8"
              w="100%"
              alignItems="flex-end"
            >
              <NumberInput
                label="Reservas por dia"
                error={errors.maxDayBookings}
                {...register('maxDayBookings')}
              />

              <NumberInput
                label="Reservas de convidados por dia"
                error={errors.maxGuestDayBookings}
                {...register('maxGuestDayBookings')}
              />

              <MaskedInput
                label="Preço da reserva de convidados"
                mask="money"
                error={errors.guestBookingPrice}
                {...register('guestBookingPrice')}
              />
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={handleCloseModal}>
              Cancel
            </Button>

            <Button
              colorScheme="green"
              type="submit"
              isLoading={formState.isSubmitting}
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
